import React from "react";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Divider,
    Stack,
    Typography,
} from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import { fToNow } from "../../../utils/formatTime";
import OrderItemsList from "./OrderItemsList";
import { getOrderTitle, getItemsQtyTotal, isOrderLate } from "../utils";

export default function KitchenOrderCard({
    order,
    itemsForCenter,
    flash,
    thresholdMinutes,
    foodLateTimeEnabled,
    lastFoodReadyAt,
    nowTs,
    distanceLocale,
    t,
    showItemsOnKds,
    onAdvanceItem,
    isItemDisabled,
    getNextStatus,
    onMarkReady,
    onShowDetails,
    isMarkReadyLoading,
}) {
    const createdAt = order?.creation
        ? new Date(order.creation.replace(" ", "T"))
        : null;
    const timerRefAt = lastFoodReadyAt || createdAt;
    const totalQty = getItemsQtyTotal(itemsForCenter, order?.entry_items);
    const late = isOrderLate({
        order,
        lastFoodReadyAt,
        thresholdMinutes,
        enabled: foodLateTimeEnabled,
        nowMs: nowTs,
    });

    return (
        <Card
            elevation={0}
            sx={{
                border: "2px solid",
                borderColor: late ? "error.main" : "warning.main",
                bgcolor: late ? "error.50" : "warning.50",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                ...(flash
                    ? {
                          animation: "kdsLateFlash 0.5s ease-in-out 0s 6",
                      }
                    : null),
                "@keyframes kdsLateFlash": {
                    "0%, 100%": {
                        boxShadow: "0 0 0 0 rgba(211, 47, 47, 0)",
                        transform: "scale(1)",
                    },
                    "50%": {
                        boxShadow: "0 0 0 6px rgba(211, 47, 47, 0.35)",
                        transform: "scale(1.01)",
                    },
                },
            }}
        >
            <CardContent
                sx={{
                    pb: 1,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Stack spacing={1.25}>
                    <Stack spacing={0.75}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 1,
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 900,
                                        lineHeight: 1.1,
                                        overflowWrap: "anywhere",
                                        fontSize: 28,
                                    }}
                                >
                                    {getOrderTitle(
                                        order,
                                        t("kds.orderFallback")
                                    )}
                                </Typography>
                                {order?.table_description && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {order.table_description}
                                    </Typography>
                                )}
                            </Box>
                            <Chip
                                size="small"
                                color={late ? "error" : "warning"}
                                label={late ? t("kds.late") : t("kds.pending")}
                            />
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                            }}
                        >
                            <Chip
                                size="small"
                                label={t("kds.itemsCount", {
                                    count: totalQty,
                                })}
                            />
                            <Chip
                                size="medium"
                                label={
                                    timerRefAt
                                        ? fToNow(timerRefAt, distanceLocale)
                                        : "—"
                                }
                                sx={{
                                    fontWeight: 900,
                                    fontSize: 16,
                                    height: 34,
                                }}
                            />
                        </Box>
                    </Stack>
                    {showItemsOnKds ? (
                        <>
                            <Divider />
                            <Box sx={{ pr: 0.25 }}>
                                <OrderItemsList
                                    items={itemsForCenter}
                                    t={t}
                                    order={order}
                                    showItemStatus
                                    onAdvanceItem={onAdvanceItem}
                                    isItemDisabled={isItemDisabled}
                                    getNextStatus={getNextStatus}
                                    distanceLocale={distanceLocale}
                                />
                            </Box>
                        </>
                    ) : null}
                </Stack>
            </CardContent>
            <CardActions
                sx={{
                    pt: 0,
                    px: 2,
                    pb: 2,
                    gap: 1,
                    mt: "auto",
                }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    color="success"
                    startIcon={<CheckCircleOutline />}
                    onClick={() => onMarkReady(order)}
                    disabled={isMarkReadyLoading}
                    sx={{ fontWeight: 900 }}
                >
                    {t("kds.readyAll")}
                </Button>
                <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={() => onShowDetails(order?.name)}
                    sx={{ fontWeight: 900 }}
                >
                    {t("kds.details")}
                </Button>
            </CardActions>
        </Card>
    );
}
